import { ReactElement, ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  RenderProps,
} from '@ff-it/ui';
import { Form, FormError, SubmissionErrors, Submit, SubmitHandler } from '@ff-it/form';

type TitleOrHeader =
  | {
      title?: ReactNode;
      header?: never;
    }
  | {
      title?: never;
      header?: ReactNode;
    };

type DialogFormProps = {
  children: ReactNode;

  onRemove?: () => Promise<void | false>;
  className?: string;
  submitLabel?: ReactNode;
  // FIXME: we are not picking form props because final-form FormProps has [otherProp: string]: any index signature.
  disabled?: boolean;
  decorators?: any;
  initialValues?: any;
  validate?: any;
} & TitleOrHeader;

export function DialogForm<R = void>({
  onDismiss,
  onSubmit,
  title,
  header,
  children,
  decorators,
  initialValues,
  submitHandler,
  onRemove,
  className,
  disabled,
  validate,
  submitLabel = 'Submit',
}: DialogFormProps &
  RenderProps<R> & {
    // submitHanlder and resolves to void. Or resolves to form values
    submitHandler?: R extends void ? SubmitHandler<any> : never;
  }): ReactElement {
  return (
    <Form
      noValidate
      autoComplete="off"
      onSubmit={async (values, form) => {
        if (submitHandler) {
          const err = await Promise.resolve(submitHandler(values, form));

          if (err) {
            return err;
          }
          onSubmit(undefined);
        } else {
          onSubmit(values as R);
        }
      }}
      initialValues={initialValues}
      renderErrors={false}
      decorators={decorators}
      className={className}
      disabled={disabled}
      display="contents"
      validate={validate}
    >
      {header}
      {title && <DialogHeader title={title} />}
      <DialogBody>
        <FormError />
        {children}
      </DialogBody>
      <DialogFooter>
        <DialogClose />
        {onRemove && (
          <Button
            type="button"
            variant="danger"
            ml="1"
            onClick={() =>
              onRemove().then((r) => {
                // false is cancel
                if (r !== false) {
                  onDismiss();
                }
              })
            }
          >
            Delete
          </Button>
        )}
        {!disabled && <Submit ml="auto">{submitLabel}</Submit>}
      </DialogFooter>
    </Form>
  );
}

type RenderDialogFormProps = {
  onClose: () => void;
  testId?: string;
  submitHandler: (values: any) => Promise<SubmissionErrors | void>;
};

export function RenderDialogForm({ onClose, testId, ...rest }: RenderDialogFormProps & DialogFormProps): ReactElement {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent testId={testId}>
        <DialogForm {...rest} onDismiss={onClose} onSubmit={onClose} />
      </DialogContent>
    </Dialog>
  );
}
